<template>
  <div class="upload-button-wrapper">
    <a-upload
        name="file"
        action=""
        @remove="handleRemove"
        :accept="props.accept"
        :maxCount="props.maxCount"
        :beforeUpload="beforeUpload"
        :file-list="fileList"
        @change="handleChange"
        :multiple="props.maxCount > 1 || props.maxCount === null"
    >
      <a-button type="primary" ghost>
        <span style="color: rgb(30, 142, 242);cursor: pointer;">
          {{ updateTitle }} <LoadingOutlined v-if="updateStatus=='上传中'"/>
        </span>
      </a-button>
    </a-upload>

    <div v-if="showFileList" class="upload-file-list">
      <div v-for="file in fileList" :key="file.uid" class="file-item">
        <span class="file-name">{{ file.name }}</span>
        <div class="file-actions ml30">
          <a-button type="link" size="small" @click="handlePreview(file)">
            <eye-outlined class="mr2" />
            预览
          </a-button>
          <a-button class="ml10" type="link" size="small" @click="handleRemove(file)">
            <delete-outlined class="mr2" />
            移除
          </a-button>
        </div>
      </div>
    </div>

    <!-- 文件预览抽屉 -->
    <FilePreview
      v-model:visible="previewVisible"
      :file-url="previewFileUrl"
      :file-name="previewFileName"
      @close="handlePreviewClose"
    />
  </div>
</template>

<script setup>
// 导入所需的依赖
import { ref, defineProps, defineEmits, defineExpose, watch } from 'vue'
import { uploadOneFile } from "@/api/upload.js";
import { message, Upload } from "ant-design-vue";
import { DeleteOutlined, EyeOutlined, LoadingOutlined } from '@ant-design/icons-vue';
import FilePreview from '@/components/FilePreview/index.vue';

const props = defineProps({
  pathName: {
    type: String,
    default: ''
  },
  accept: {
    type: String,
    default: ''
  },
  maxCount: {
    type: Number,
    default: null
  },
  updateTitle: {
    type: String,
    default: '上传'
  },
  // 控制是否显示自定义文件列表
  showCustomFileList: {
    type: Boolean,
    default: true
  }
})

let fileList = ref([])
let updateTitle = ref(props.updateTitle || "选择上传");
let updateStatus = ref("");
let showFileList = ref(false);
let uploadingFiles = ref(0); // 跟踪正在上传的文件数量
let previewVisible = ref(false);
let previewFileUrl = ref('');
let previewFileName = ref('');

const emits = defineEmits(['uploadFinished', 'fileListChange'])

// 监听 updateTitle 属性变化
watch(() => props.updateTitle, (newVal) => {
  updateTitle.value = newVal || "选择上传";
});

// 文件体转化
const convertFormData = (data) => {
  let folder = Date.now().toString();
  const json = {
    bucketName: "testing-management-private",
    pathName: props.pathName + '/' + folder, // 上传服务器路径，以项目模块名定义 如 test_project test_task
  };
  const formData = new FormData();
  formData.append("uploadFile", data);
  formData.append(
    "json",
    new Blob([JSON.stringify(json)], { type: "application/json" })
  );
  return formData;
};

// 处理文件上传前的逻辑
const beforeUpload = (file, currentFileList) => {
  if (props.accept !== '') {
    const docsArr = props.accept?.split(',').map(type => type.toLowerCase());
    const fileType = file.name.split(".");
    const fileDate = fileType.slice(-1);
    if (!docsArr.includes(`.${fileDate[0].toLowerCase()}`)) { // 转换文件扩展名为小写
      message.error(`仅支持${props.accept}文件格式！`);
      return Upload.LIST_IGNORE;
    }
  }

  // 检查是否超过最大上传数量
  if (props.maxCount !== null && fileList.value.length >= props.maxCount) {
    message.error(`最多只能上传${props.maxCount}个文件！`);
    return Upload.LIST_IGNORE;
  }

  // 添加上传中的文件到列表
  const uploadingFile = {
    uid: file.uid,
    name: file.name,
    status: 'uploading',
    percent: 0
  };

  // 检查是否已存在相同uid的文件
  const existingIndex = fileList.value.findIndex(item => item.uid === file.uid);
  if (existingIndex > -1) {
    fileList.value[existingIndex] = uploadingFile;
  } else {
    fileList.value.push(uploadingFile);
  }

  showFileList.value = fileList.value.length > 0;
  uploadingFiles.value++;
  updateStatus.value = "上传中";

  // 上传文件
  uploadOneFile(convertFormData(file)).then(res => {
    // 更新文件状态为已完成
    const index = fileList.value.findIndex(item => item.uid === file.uid);
    if (index > -1) {
      fileList.value[index] = {
        uid: file.uid,
        name: file.name,
        status: 'done',
        url: res.data,
        percent: 100
      };
    }

    emits('uploadFinished', res.data);

    uploadingFiles.value--;
    if (uploadingFiles.value === 0) {
      updateStatus.value = "完成";
    }

    // 通知父组件文件列表变化
    emits('fileListChange', fileList.value);
  }).catch(err => {
    // 更新文件状态为失败
    const index = fileList.value.findIndex(item => item.uid === file.uid);
    if (index > -1) {
      fileList.value[index] = {
        uid: file.uid,
        name: file.name,
        status: 'error'
      };
    }

    message.error(`文件 ${file.name} 上传失败: ${err}`);

    uploadingFiles.value--;
    if (uploadingFiles.value === 0) {
      updateStatus.value = "";
    }

    // 通知父组件文件列表变化
    emits('fileListChange', fileList.value);
  });

  return false; // 阻止自动上传，使用自定义上传逻辑
};

// 处理文件列表变化
const handleChange = ({ fileList: newFileList }) => {
  // 过滤掉已在列表中的文件（避免重复）
  const existingUids = fileList.value.map(file => file.uid);
  const newFiles = newFileList.filter(file => !existingUids.includes(file.uid));

  if (newFiles.length > 0) {
    fileList.value = [...fileList.value, ...newFiles];
    showFileList.value = fileList.value.length > 0;
    emits('fileListChange', fileList.value);
  }
};

// 处理文件移除
const handleRemove = (file) => {
  const index = fileList.value.findIndex(item => item.uid === file.uid);
  if (index > -1) {
    fileList.value.splice(index, 1);
    showFileList.value = fileList.value.length > 0;

    // 如果没有文件了，重置上传按钮文字
    if (fileList.value.length === 0) {
      updateTitle.value = props.updateTitle || "选择上传";
      updateStatus.value = "";
    }

    emits('fileListChange', fileList.value);
  }
  return true;
};

// 处理文件预览
const handlePreview = (file) => {
  previewFileUrl.value = file.url;
  previewFileName.value = file.name;
  previewVisible.value = true;
};

// 处理预览关闭
const handlePreviewClose = () => {
  previewVisible.value = false;
};

const clearFileList = () => {
  fileList.value = [];
  showFileList.value = false;
  updateTitle.value = props.updateTitle || "选择上传";
  updateStatus.value = "";
  uploadingFiles.value = 0;
  emits('fileListChange', fileList.value);
};

// 设置文件列表（用于编辑模式下显示已有的文件）
const setFileList = (files) => {
  if (files && Array.isArray(files)) {
    fileList.value = files.map(file => ({
      uid: file.id || file.uid,
      name: file.fileName || file.name,
      status: 'done',
      url: file.fileUrl,
      response: { data: file }
    }));
    showFileList.value = fileList.value.length > 0;

    // 如果有文件，更新上传按钮文字
    if (fileList.value.length > 0) {
      updateTitle.value = "重新上传";
    }

    emits('fileListChange', fileList.value);
  }
};

defineExpose({
  clearFileList,
  setFileList,
  fileList
});
</script>

<style lang="less" scoped>
.upload-button-wrapper {
  display: inline-block;
  position: relative;
}

.upload-file-list {
  margin-top: 10px;
  padding: 10px;
  background-color: #f9f9f9;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  min-width: 300px;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  &:last-child {
    border-bottom: none;
  }
}

.file-name {
  color: #1890ff;
  font-size: 14px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.ant-upload-list) {
  display: none; // 隐藏默认的上传列表，使用自定义的文件列表
}

// 上传进度条样式
:deep(.ant-progress) {
  margin: 4px 0;
}
</style>